import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./RoyalLogo.png";
import defaultProfile from "./profileavartar.jpg";

/* ==========================================================================
   ROYAL PRIESTHOOD ACADEMY SCHOOL SYSTEM
   Official Teacher Data PDF
   Theme: Royal Blue • Sky Blue • White • Charcoal
   Layout: A4 Landscape
   ========================================================================== */

const COLORS = {
    royalBlue: "#174A91",
    deepBlue: "#10366B",
    skyBlue: "#72BCE8",
    lightBlue: "#EAF5FC",
    paleBlue: "#F5FAFE",
    white: "#FFFFFF",
    black: "#202833",
    darkGray: "#424D5A",
    gray: "#687586",
    lightGray: "#DCE6EF",
    border: "#D6E3EF",
    softGray: "#F7F9FC",
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 22,
        paddingHorizontal: 27,
        paddingBottom: 36,
        backgroundColor: COLORS.white,
        fontFamily: "Helvetica",
        color: COLORS.black,
    },

    /* ========================= HEADER ========================= */

    header: {
        minHeight: 91,
        backgroundColor: COLORS.paleBlue,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderBottomWidth: 3,
        borderBottomColor: COLORS.royalBlue,
        paddingHorizontal: 15,
        paddingVertical: 10,
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 13,
        borderRadius: 5,
    },

    logoContainer: {
        width: 76,
        height: 69,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderColor: COLORS.lightGray,
        borderRadius: 5,
        padding: 4,
    },

    logo: {
        width: 64,
        height: 60,
        objectFit: "contain",
    },

    headerInfo: {
        marginLeft: 13,
        flex: 1,
        justifyContent: "center",
        paddingRight: 8,
    },

    schoolName: {
        fontSize: 16,
        fontWeight: "bold",
        color: COLORS.deepBlue,
        marginBottom: 5,
        letterSpacing: 0.25,
    },

    headerText: {
        fontSize: 8,
        color: COLORS.darkGray,
        marginBottom: 3,
        lineHeight: 1.35,
    },

    portalLabel: {
        fontSize: 7.5,
        color: COLORS.royalBlue,
        fontWeight: "bold",
        marginTop: 1,
    },

    titleBox: {
        width: 174,
        alignItems: "flex-end",
        justifyContent: "center",
        borderLeftWidth: 1,
        borderLeftColor: COLORS.border,
        paddingLeft: 12,
        minHeight: 55,
    },

    documentTitle: {
        fontSize: 10,
        fontWeight: "bold",
        color: COLORS.royalBlue,
        textAlign: "right",
        lineHeight: 1.4,
    },

    documentSubTitle: {
        fontSize: 8,
        color: COLORS.gray,
        marginTop: 5,
        textAlign: "right",
    },

    documentBadge: {
        marginTop: 6,
        backgroundColor: COLORS.lightBlue,
        borderWidth: 1,
        borderColor: "#C8E5F7",
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 3,
    },

    badgeText: {
        fontSize: 6.5,
        color: COLORS.royalBlue,
        fontWeight: "bold",
    },

    /* ========================= MAIN LAYOUT ========================= */

    mainRow: {
        flexDirection: "row",
        alignItems: "stretch",
        marginBottom: 10,
    },

    profileCard: {
        width: 218,
        backgroundColor: COLORS.paleBlue,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderLeftWidth: 4,
        borderLeftColor: COLORS.royalBlue,
        paddingHorizontal: 12,
        paddingTop: 12,
        paddingBottom: 10,
        alignItems: "center",
        borderRadius: 5,
    },

    profileImageFrame: {
        width: 91,
        height: 91,
        borderRadius: 46,
        backgroundColor: COLORS.white,
        borderWidth: 3,
        borderColor: COLORS.skyBlue,
        padding: 3,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 8,
    },

    profileImage: {
        width: 79,
        height: 79,
        borderRadius: 40,
        objectFit: "cover",
    },

    teacherName: {
        fontSize: 13,
        fontWeight: "bold",
        color: COLORS.deepBlue,
        textAlign: "center",
        marginBottom: 3,
        lineHeight: 1.35,
    },

    employeeLabel: {
        fontSize: 7,
        color: COLORS.gray,
        textAlign: "center",
        marginBottom: 10,
    },

    profileDivider: {
        width: "100%",
        height: 1,
        backgroundColor: COLORS.border,
        marginBottom: 9,
    },

    profileInfo: {
        width: "100%",
    },

    infoRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 7,
    },

    infoLabel: {
        width: 69,
        fontSize: 7.2,
        fontWeight: "bold",
        color: COLORS.gray,
        lineHeight: 1.4,
    },

    infoValue: {
        flex: 1,
        fontSize: 7.5,
        color: COLORS.black,
        lineHeight: 1.4,
    },

    rightContent: {
        flex: 1,
        marginLeft: 12,
    },

    /* ========================= SECTION HEADERS ========================= */

    sectionHeader: {
        backgroundColor: COLORS.lightBlue,
        borderLeftWidth: 3,
        borderLeftColor: COLORS.royalBlue,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        paddingVertical: 7,
        paddingHorizontal: 9,
        marginBottom: 7,
        borderRadius: 2,
        flexDirection: "row",
        alignItems: "center",
    },

    sectionHeaderText: {
        fontSize: 8.5,
        fontWeight: "bold",
        color: COLORS.deepBlue,
        letterSpacing: 0.45,
    },

    sectionNumber: {
        fontSize: 7,
        color: COLORS.royalBlue,
        fontWeight: "bold",
        marginRight: 7,
    },

    /* ========================= CONTENT CARDS ========================= */

    card: {
        backgroundColor: COLORS.white,
        borderWidth: 1,
        borderColor: COLORS.border,
        paddingHorizontal: 11,
        paddingVertical: 10,
        marginBottom: 10,
        borderRadius: 4,
    },

    assignmentRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 5,
        borderBottomWidth: 0.6,
        borderBottomColor: COLORS.lightGray,
    },

    assignmentRowLast: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingTop: 5,
    },

    assignmentLabel: {
        width: 108,
        fontSize: 7.8,
        fontWeight: "bold",
        color: COLORS.gray,
        lineHeight: 1.5,
    },

    assignmentValue: {
        flex: 1,
        fontSize: 8,
        color: COLORS.black,
        lineHeight: 1.5,
    },

    /* ========================= RULES AND REGULATIONS ========================= */

    regulationGrid: {
        flexDirection: "row",
    },

    regulationColumn: {
        flex: 1,
        paddingRight: 8,
    },

    regulationColumnRight: {
        flex: 1,
        paddingLeft: 10,
        borderLeftWidth: 1,
        borderLeftColor: COLORS.lightGray,
    },

    regulationItem: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 8,
    },

    regulationNumber: {
        width: 19,
        height: 15,
        backgroundColor: COLORS.lightBlue,
        borderRadius: 3,
        textAlign: "center",
        paddingTop: 3,
        color: COLORS.royalBlue,
        fontSize: 6.8,
        fontWeight: "bold",
        marginRight: 6,
    },

    regulationText: {
        flex: 1,
        fontSize: 7.6,
        color: COLORS.darkGray,
        lineHeight: 1.45,
    },

    /* ========================= AUTHORIZATION ========================= */

    authorization: {
        backgroundColor: COLORS.paleBlue,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderTopWidth: 2,
        borderTopColor: COLORS.royalBlue,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginTop: 0,
        borderRadius: 4,
    },

    authorizationHeading: {
        fontSize: 8.5,
        color: COLORS.deepBlue,
        fontWeight: "bold",
        letterSpacing: 0.4,
        marginBottom: 4,
    },

    authorizationNote: {
        fontSize: 7,
        color: COLORS.gray,
        lineHeight: 1.4,
    },

    signatureRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 18,
        paddingHorizontal: 5,
    },

    signatureBox: {
        width: "40%",
        borderTopWidth: 1,
        borderTopColor: COLORS.gray,
        paddingTop: 5,
        alignItems: "center",
    },

    signatureText: {
        fontSize: 7.2,
        fontWeight: "bold",
        color: COLORS.darkGray,
        textAlign: "center",
    },

    signatureSubText: {
        fontSize: 6.5,
        color: COLORS.gray,
        textAlign: "center",
        marginTop: 3,
    },

    /* ========================= FOOTER ========================= */

    footer: {
        position: "absolute",
        bottom: 15,
        left: 27,
        right: 27,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        paddingTop: 6,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    footerText: {
        fontSize: 6.8,
        color: COLORS.gray,
    },

    footerBrand: {
        fontSize: 7,
        color: COLORS.royalBlue,
        fontWeight: "bold",
    },

    footerRight: {
        flexDirection: "row",
        alignItems: "center",
    },

    footerDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: COLORS.skyBlue,
        marginRight: 5,
    },
});

const TeacherDataPDF = ({
    teacher = {},
    divisionName,
    subjectNames,
    gradeNames,
}) => {
    const profileImage = teacher?.profile_image_url || defaultProfile;

    const displayValue = (value, fallback = "N/A") => {
        if (value === null || value === undefined || value === "") {
            return fallback;
        }
        return String(value);
    };

    const rulesLeft = [
        "Be punctual and attend school regularly.",
        "Prepare and deliver effective lessons.",
        "Respect students, parents, and colleagues.",
        "Maintain professional boundaries.",
        "Protect school property and resources.",
    ];

    const rulesRight = [
        "Perform duties with commitment and integrity.",
        "Be a positive role model.",
        "Maintain a clean learning environment.",
        "Maintain professional appearance.",
        "Follow school policies and communication procedures.",
    ];

    const renderRule = (rule, index, offset = 0) => (
        <View style={styles.regulationItem} key={`${offset}-${index}`}>
            <Text style={styles.regulationNumber}>
                {index + offset + 1}
            </Text>
            <Text style={styles.regulationText}>{rule}</Text>
        </View>
    );

    return (
        <Document
            title={`Teacher Data - ${displayValue(
                teacher.full_name,
                "Academic Staff"
            )}`}
            author="Royal Priesthood Academy School System"
            subject="Official Employee Data Sheet"
        >
            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
                wrap={false}
            >
                {/* ==================== SCHOOL HEADER ==================== */}

                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        <Image src={schoolLogo} style={styles.logo} />
                    </View>

                    <View style={styles.headerInfo}>
                        <Text style={styles.schoolName}>
                            Royal Priesthood Academy School System
                        </Text>

                        <Text style={styles.headerText}>
                            New Jerusalem Community, Johnsonville, Monrovia, Liberia
                        </Text>

                        <Text style={styles.portalLabel}>
                            SCHOOL PORTAL
                        </Text>

                        <Text style={styles.headerText}>
                            www.royalpriesthoodacademyinc.com
                        </Text>
                    </View>

                    <View style={styles.titleBox}>
                        <Text style={styles.documentTitle}>
                            OFFICIAL EMPLOYEE{"\n"}DATA SHEET
                        </Text>

                        <Text style={styles.documentSubTitle}>
                            Academic Staff Record
                        </Text>

                        <View style={styles.documentBadge}>
                            <Text style={styles.badgeText}>
                                CONFIDENTIAL STAFF RECORD
                            </Text>
                        </View>
                    </View>
                </View>

                {/* ==================== PROFILE AND ASSIGNMENTS ==================== */}

                <View style={styles.mainRow}>
                    {/* Teacher profile */}

                    <View style={styles.profileCard}>
                        <View style={styles.profileImageFrame}>
                            <Image
                                src={profileImage}
                                style={styles.profileImage}
                            />
                        </View>

                        <Text style={styles.teacherName}>
                            {displayValue(teacher.full_name, "Teacher Name")}
                        </Text>

                        <Text style={styles.employeeLabel}>
                            ACADEMIC STAFF PROFILE
                        </Text>

                        <View style={styles.profileDivider} />

                        <View style={styles.profileInfo}>
                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>
                                    Qualification
                                </Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.qualification)}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>Email</Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.email)}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>
                                    Secure Password
                                </Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.initial_password)}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>Contact</Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.contact)}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>Salary</Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.salary)}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.infoLabel}>Date Added</Text>
                                <Text style={styles.infoValue}>
                                    {displayValue(teacher.date_add)}
                                </Text>
                            </View>
                        </View>
                    </View>

                    {/* Assignment and regulation sections */}

                    <View style={styles.rightContent}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionNumber}>01</Text>
                            <Text style={styles.sectionHeaderText}>
                                TEACHING ASSIGNMENTS
                            </Text>
                        </View>

                        <View style={styles.card}>
                            <View style={styles.assignmentRow}>
                                <Text style={styles.assignmentLabel}>
                                    Division
                                </Text>
                                <Text style={styles.assignmentValue}>
                                    {displayValue(divisionName)}
                                </Text>
                            </View>

                            <View style={styles.assignmentRow}>
                                <Text style={styles.assignmentLabel}>
                                    Subjects
                                </Text>
                                <Text style={styles.assignmentValue}>
                                    {displayValue(
                                        subjectNames,
                                        "No subjects assigned"
                                    )}
                                </Text>
                            </View>

                            <View style={styles.assignmentRow}>
                                <Text style={styles.assignmentLabel}>
                                    Grade Classes
                                </Text>
                                <Text style={styles.assignmentValue}>
                                    {displayValue(
                                        gradeNames,
                                        "No grade classes assigned"
                                    )}
                                </Text>
                            </View>

                            <View style={styles.assignmentRowLast}>
                                <Text style={styles.assignmentLabel}>
                                    Dependent Student
                                </Text>
                                <Text style={styles.assignmentValue}>
                                    {displayValue(
                                        teacher.dependent_student,
                                        "None"
                                    )}
                                </Text>
                            </View>
                        </View>

                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionNumber}>02</Text>
                            <Text style={styles.sectionHeaderText}>
                                EMPLOYEE RULES AND REGULATIONS
                            </Text>
                        </View>

                        <View style={styles.card}>
                            <View style={styles.regulationGrid}>
                                <View style={styles.regulationColumn}>
                                    {rulesLeft.map((rule, index) =>
                                        renderRule(rule, index)
                                    )}
                                </View>

                                <View style={styles.regulationColumnRight}>
                                    {rulesRight.map((rule, index) =>
                                        renderRule(rule, index, 5)
                                    )}
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ==================== AUTHORIZATION ==================== */}

                <View style={styles.authorization}>
                    <Text style={styles.authorizationHeading}>
                        03  AUTHORIZATION AND ACKNOWLEDGMENT
                    </Text>

                    <Text style={styles.authorizationNote}>
                        This document records the teacher's staff information
                        and assigned responsibilities. Signatures below
                        acknowledge review and administrative authorization.
                    </Text>

                    <View style={styles.signatureRow}>
                        <View style={styles.signatureBox}>
                            <Text style={styles.signatureText}>
                                Teacher Signature
                            </Text>
                            <Text style={styles.signatureSubText}>
                                Date: ____________________
                            </Text>
                        </View>

                        <View style={styles.signatureBox}>
                            <Text style={styles.signatureText}>
                                Authorized Administrator
                            </Text>
                            <Text style={styles.signatureSubText}>
                                Date: ____________________
                            </Text>
                        </View>
                    </View>
                </View>

                {/* ==================== SCHOOL FOOTER ==================== */}

                <View style={styles.footer} fixed>
                    <Text style={styles.footerBrand}>
                        ROYAL PRIESTHOOD ACADEMY
                    </Text>

                    <View style={styles.footerRight}>
                        <View style={styles.footerDot} />
                        <Text style={styles.footerText}>
                            Official Teacher Record - Powered by: Polu Integrated School Management System
                        </Text>
                    </View>

                    <Text style={styles.footerText}>
                        School Management System
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export default TeacherDataPDF;
